import UIComponent from "../ui/component";
import api from "../api";
import sanitize from "sanitize-html";
import {
    disableAiAccess,
    isAiAccessAllowed,
    isSubscriptionGateEnabled,
} from "../utils/aiAccess";

export class ResultTabs extends UIComponent {
    #onChangeCbs = [];
    #onDeleteCbs = [];

    init() {
        this.includeComponents({
            confirmDeleteTabModal: "document@#ConfirmDeleteTabModal",
            editBtn: "document@.edit-btn",
            modalContent: "document@.modal-ai-templates__body",
            modalAI: "document@#modalAiTemplates",
            resultTabsScroller: "document@.result-tabs__desktop",
            subscriptionModal: "document@#SubscriptionModal",
        });

        this.contentWrapper = document.querySelector(
            ".transcription-content-wrapper"
        );

        if (this.editBtn) {
            this.editBtn.on("click", () =>
                this.createTab(this.editBtn.data("bs-store-url"))
            );
        }

        this.confirmDeleteTabModal?.confirmBtn?.on(
            "click",
            this.handleConfirmDelete.bind(this)
        );

        this.bindAiTemplates();
        this.syncMobileDropdown();
    }

    getAiTemplateLanguage() {
        const roots = Array.from(
            document.querySelectorAll(
                '[data-tbx-component="AITemplateLanguage"][data-tbx-name="aiTemplateLanguage"]'
            )
        );
        const root =
            roots.find((el) => el && el.offsetParent !== null) || roots[0];
        const active = root?.querySelector?.(
            '.language-choice__custom-select__select-option.active[data-language]'
        );
        return active?.dataset?.language;
    }

    ensureVisible() {
        if (this.hasClass?.("d-none")) this.removeClass("d-none");
        this.element?.classList?.remove?.("d-none");
        document
            .querySelector("[data-result-tabs-divider]")
            ?.classList?.remove("d-none");
        this.resultTabsScroller?.queueUpdate?.();
    }

    ensureHiddenIfNoCopies() {
        const hasCopies = this.hasAnyCopyTabs();
        if (hasCopies) return;
        this.addClass?.("d-none");
        this.element?.classList?.add?.("d-none");
        document
            .querySelector("[data-result-tabs-divider]")
            ?.classList?.add("d-none");
        this.resultTabsScroller?.queueUpdate?.();
    }

    hasAnyCopyTabs() {
        const listRoot =
            this.list?.element || this.list?.el?.() || this.element;
        const items =
            listRoot?.querySelectorAll?.(
                '.result-tabs__item[data-id]:not([data-id="0"])'
            ) || [];
        return items.length > 0;
    }

    handleTabSelect(tabId) {
        this.changeActiveTabById(tabId);
    }

    handleTabDeleteRequest(detail) {
        this.toDelete = detail;
        this.confirmDeleteTabModal?.show(detail?.tabName);
    }

    async createAiTab(item) {
        if (!isAiAccessAllowed() && isSubscriptionGateEnabled()) {
            if (this.subscriptionModal?.element?.dataset) {
                this.subscriptionModal.element.dataset.titleMode = "ai";
            }
            this.subscriptionModal?.show();
            return null;
        }
        const url = item.promtBtn.url;

        try {
            this.setButtonsLoading(false);

            const activeTabId = this.getActiveTabId();
            const sourceVariantId = this.isCopyTab(activeTabId)
                ? parseInt(String(activeTabId), 10)
                : null;
            const language = this.getAiTemplateLanguage();
            const data = sourceVariantId
                ? await api.post(url, { variantId: sourceVariantId, language: language })
                : await api.post(url, { language: language });

            if (isSubscriptionGateEnabled()) disableAiAccess();

            if (data.status === "pending") {
                this.setButtonsLoading(true);
            }
            const tabId = data.transcription_variant_id;
            if (!tabId) throw new Error(`Tab ID is: ${tabId}`);

            const tabName = data.tab_name;

            const storeUrl = this.editBtn?.data("bs-store-url");
            const deleteUrl = `${storeUrl}/${tabId}`;

            const isAiTab = data.is_ai_tab;
            const isAiTranslate = data.type === "ai_translate";
            this.ensureVisible();
            this.addTabToDownloadChoices(tabId, tabName);
            const li = this.createTabElement(
                tabId,
                tabName,
                deleteUrl,
                isAiTab,
                isAiTranslate
            );
            const mobileItem = this.createMobileDropdownItem(
                tabId,
                tabName,
                deleteUrl,
                isAiTab,
                isAiTranslate
            );
            const contentDiv = this.createContentElement(tabId, "", isAiTab);
            this.list?.insertAfterActive?.(li);
            this.mobileSelect?.insertAfterActive?.(mobileItem);
            if (this.contentWrapper && contentDiv) {
                this.contentWrapper.appendChild(contentDiv);
            }
            this.changeActiveTab(li);
            this.parent.statusAiPromt();
            return li;
        } catch (e) {
            console.error(e);
        } finally {
            this.setButtonsLoading(true);
        }
    }

    changeActiveTab(tabEl) {
        this.list?.setActive?.(tabEl);
        this.activeTab = tabEl;

        const tabId = tabEl.getAttribute("data-id");

        this.showContentForTab(tabId);
        this.#onChangeCbs.forEach((cb) => {
            try {
                cb(tabId);
            } catch (e) {
                console.error(e);
            }
        });
        this.syncMobileDropdown();
        this.resultTabsScroller?.scrollActiveIntoView?.();

        this.emit("tabChange", {
            id: tabId,
        }, {
            bubbles: true,
        });
    }

    showContentForTab(tabId) {
        if (!this.contentWrapper) return;
        this.contentWrapper
            .querySelectorAll("[data-tab-content]")
            .forEach((el) => el.classList.add("d-none"));
        this.contentWrapper
            .querySelector(`[data-tab-content="${tabId}"]`)
            ?.classList.remove("d-none");
    }

    async handleConfirmDelete() {
        const { tabId: requestedTabId, deleteUrl } = this.toDelete || {};
        if (!deleteUrl) return;

        try {
            const data = await api.delete(deleteUrl);
            const tabId = data.transcription_variant_id;
            const wasActive =
                String(this.getActiveTabId()) === String(requestedTabId) ||
                String(this.getActiveTabId()) === String(tabId);

            this.removeTab(tabId);
            this.removeTabFromDownloadChoices(tabId);
            this.resultTabsScroller?.queueUpdate?.();

            if (wasActive) {
                const fallback =
                    this.list?.find?.('[data-id="0"]') ||
                    this.list?.find?.(".result-tabs__item") ||
                    this.element.querySelector(".result-tabs__item");
                if (fallback) this.changeActiveTab(fallback);
            }

            if (!this.hasAnyCopyTabs()) {
                this.changeActiveTabById?.("0");
                this.ensureHiddenIfNoCopies();
            }

            this.#onDeleteCbs.forEach((cb) => {
                try {
                    cb(tabId);
                } catch (e) {
                    console.error(e);
                }
            });
        } catch (e) {
            console.error(e);
        } finally {
            this.toDelete = null;
            this.confirmDeleteTabModal?.hide();
        }
    }

    async createTab(storeUrl) {
        const contentHtml = sanitize(this.getActiveTabContentHtml(), {
            allowedTags: ["div", "span", "p", "br"],
            allowedAttributes: {
                span: ["data-start-time", "data-end-time", "style"],
                div: ["style"],
            },
        });

        try {
            const formData = new FormData();
            formData.append("content", contentHtml);

            const data = await api.post(storeUrl, formData);

            const tabId = data.transcription_variant_id;
            if (!tabId) throw new Error(`Tab ID is: ${tabId}`);

            const deleteUrl = `${storeUrl}/${tabId}`;
            this.ensureVisible();
            this.addTabToDownloadChoices(tabId, data.title);

            const li = this.createTabElement(tabId, data.title, deleteUrl);
            const mobileItem = this.createMobileDropdownItem(
                tabId,
                data.title,
                deleteUrl
            );
            const contentDiv = this.createContentElement(
                tabId,
                data.content || contentHtml
            );

            this.list?.insertAfterActive?.(li);
            this.mobileSelect?.insertAfterActive?.(mobileItem);
            if (this.contentWrapper && contentDiv) {
                this.contentWrapper.appendChild(contentDiv);
            }

            this.parent.completedResult.add(tabId);
            requestAnimationFrame(() => this.changeActiveTab(li));

            return li;
        } catch (e) {
            console.error(e);
            return null;
        } finally {
            this.editBtn?.removeClass("disabled");
            this.editBtn?.el()?.removeAttribute("disabled");
        }
    }

    addTabToDownloadChoices(tabId, title) {
        const tbx = this.tbx || window.Tbx;
        if (!tbx?.getComponentByElement) return;
        document
            .querySelectorAll('[data-tbx-component="DownloadChoice"]')
            .forEach((el) => {
                let comp = tbx.getComponentByElement(el);
                if (!comp && tbx.createComponent) {
                    comp = tbx.createComponent(el);
                    tbx.initQueued?.();
                }
                comp?.addOption?.({ tabId, title });
            });
    }

    removeTabFromDownloadChoices(tabId) {
        const tbx = this.tbx || window.Tbx;
        if (!tbx?.getComponentByElement) return;
        document
            .querySelectorAll('[data-tbx-component="DownloadChoice"]')
            .forEach((el) => {
                let comp = tbx.getComponentByElement(el);
                if (!comp && tbx.createComponent) {
                    comp = tbx.createComponent(el);
                    tbx.initQueued?.();
                }
                comp?.removeOption?.(tabId);
            });
    }

    createTabElement(id, title, url, isAiTab = false, isAiTranslate = false) {
        const tpl = this.tab?.el ? this.tab.el() : this.tab;
        if (!tpl) return null;
        const li = tpl.content.firstElementChild.cloneNode(true);
        li.dataset.id = String(id);
        li.dataset.tabUpdateUrl = url;
        li.dataset.isAiTab = isAiTab ? "1" : "0";

        const tabBtn = li.querySelector(".result-tabs__tab");
        if (tabBtn) {
            tabBtn.textContent = title;
            if (isAiTranslate) {
                tabBtn.className = "result-tabs__tab ai-translate-btn";
                tabBtn.style.paddingInlineStart = "24px";
            } else if (isAiTab) {
                tabBtn.className = "result-tabs__tab ai-btn";
                tabBtn.style.paddingInlineStart = "24px";
            }
        }

        const deleteBtn = li.querySelector(".result-tabs__delete");
        if (deleteBtn) deleteBtn.dataset.tabUrl = url;

        return li;
    }

    createMobileDropdownItem(
        id,
        title,
        url,
        isAiTab = false,
        isAiTranslate = false
    ) {
        const tpl = this.tab_mobile?.el
            ? this.tab_mobile.el()
            : this.tab_mobile;
        if (!tpl) return null;
        const li = tpl.content.firstElementChild.cloneNode(true);

        li.dataset.id = String(id);
        li.dataset.tabUpdateUrl = url;
        li.dataset.tabUrl = url;
        li.dataset.isAiTab = isAiTab ? "1" : "0";

        const textSpan = li.querySelector(".result-tabs__dropdown-item-text");
        if (textSpan) {
            textSpan.textContent = title;
            if (isAiTranslate) {
                textSpan.classList.add("ai-translate-btn");
                textSpan.style.paddingInlineStart = "24px";
            } else if (isAiTab) {
                textSpan.classList.add("ai-btn");
                textSpan.style.paddingInlineStart = "24px";
            }
        }

        const deleteBtn = li.querySelector(".result-tabs__dropdown-delete");
        if (deleteBtn) deleteBtn.dataset.tabUrl = url;

        return li;
    }

    createContentElement(tabId, content, isAiPreloader = false) {
        const tpl = this.tab_content?.el
            ? this.tab_content.el()
            : this.tab_content;
        if (!tpl) return null;
        const div = tpl.content.firstElementChild.cloneNode(true);
        div.dataset.tabContent = String(tabId);

        const preloader = div.querySelector(".ai_preloader");
        const textEl = div.querySelector(".text_transcriber");
        
        if (isAiPreloader) {
            // Заменяем простой спиннер на прогресс-бар
            if (preloader) {
                preloader.innerHTML = this.getVariantPreloaderHtml(tabId);
                preloader.classList.remove('d-none');
            }
            textEl?.classList.add("d-none");
            
            // Инициализируем компонент сразу (tbx не требует нахождения в DOM)
            const progressEl = div.querySelector('[data-tbx-component="ProgressVariant"]');
            if (progressEl) {
                const Tbx = this.tbx || window.Tbx;
                if (Tbx) {
                    const component = Tbx.createComponent(progressEl);
                    Tbx.createComponents(null, progressEl, component);
                    Tbx.initQueued?.();
                }
            }
        } else {
            preloader?.classList.add("d-none");
            if (textEl) textEl.innerHTML = content;
        }
        return div;
    }

    getVariantPreloaderHtml(variantId) {
        // Возвращаем HTML разметку прогресс-бара для динамического создания
        return `
            <div class="variant-progress progress-transcriber d-flex flex-column align-items-center" 
                 data-tbx-component="ProgressVariant" 
                 data-variant-id="${variantId}"
                 data-tbx-name="variantProgress_${variantId}">
                
                <div data-tbx-transparent data-tbx-name="progress_content" class="w-100 d-none">
                    <div class="d-flex flex-column align-items-center">
                        <div class="manrope-regular fs-24 text-center text-dark mt-3 mb-4 progress-title" data-tbx-name="title">
                            Обработка...
                        </div>
                        <div class="progress">
                            <div class="progress-bar" data-tbx-name="progress_bar" 
                                 role="progressbar" 
                                 aria-valuenow="0" 
                                 aria-valuemin="0" 
                                 aria-valuemax="100"
                                 style="min-width: 40px;">
                                <span></span>
                            </div>
                        </div>
                        <div class="manrope-regular fs-24 text-center text-dark mt-3 text-time" 
                             data-tbx-name="text_time"
                             data-tbx-var-end_of_time="Завершаем...">
                        </div>
                    </div>
                </div>
                
                <div class="d-none" data-tbx-name="error_content">
                    <div class="text-center">
                        <img src="/img/errors/error_format.svg" style="width:200px;">
                        <h6 class="mt-3">Ошибка обработки</h6>
                    </div>
                </div>
            </div>
        `;
    }

    removeTab(tabId) {
        this.list?.removeById?.(tabId);
        this.mobileSelect?.removeById?.(tabId);
        this.contentWrapper
            ?.querySelector(`[data-tab-content="${tabId}"]`)
            ?.remove();
    }

    onChange(cb) {
        if (typeof cb === "function") this.#onChangeCbs.push(cb);
    }
    onDelete(cb) {
        if (typeof cb === "function") this.#onDeleteCbs.push(cb);
    }

    getActiveTabElement() {
        return (
            this.list?.getActiveItem?.() ||
            this.element.querySelector(".result-tabs__item--active")
        );
    }

    getActiveTabId() {
        return this.getActiveTabElement()?.getAttribute("data-id");
    }

    isCopyTab(tabId) {
        return tabId && tabId !== "0";
    }

    isAiTab(tabId = this.getActiveTabId()) {
        if (!tabId) return false;
        const el =
            this.element.querySelector(
                `.result-tabs__item[data-id="${tabId}"]`
            ) ||
            this.element.querySelector(
                `.result-tabs__dropdown-item[data-id="${tabId}"]`
            );
        if (!el) return false;
        return (
            String(
                el.getAttribute("data-is-ai-tab") || el.dataset?.isAiTab || "0"
            ) === "1"
        );
    }

    getCopyContainer(tabId) {
        return (
            this.contentWrapper?.querySelector(
                `[data-tbx-name="copy_text"][data-tab-content="${tabId}"]`
            ) || null
        );
    }

    getCopyHtml(tabId = this.getActiveTabId()) {
        return (
            this.getCopyContainer(tabId)
                ?.querySelector("#text_transcriber")
                ?.innerHTML.trim() || null
        );
    }

    setCopyHtml(tabId, html) {
        const container = this.getCopyContainer(tabId);
        const el = container?.querySelector("#text_transcriber");
        if (el) el.innerHTML = html;
    }

    updateAiTabContent(tabId, content) {
        const container = this.getCopyContainer(tabId);
        if (!container) return;

        const preloader = container.querySelector(".ai_preloader");
        const textEl = container.querySelector(".text_transcriber");

        preloader?.classList.add("d-none");
        if (textEl) {
            textEl.classList.remove("d-none");
            textEl.innerHTML = content;
        }
        this.emit(
            "aiContentUpdated",
            { id: tabId },
            { bubbles: true }
        );
    }

    getCopyText(tabId = this.getActiveTabId()) {
        if (this.isCopyTab(tabId)) {
            const el =
                this.getCopyContainer(tabId)?.querySelector(
                    "#text_transcriber"
                );
            if (el) return el.textContent;
        }
        return document.querySelector("#text_transcriber")?.textContent || "";
    }

    getActiveTabContentHtml() {
        const tabId = this.getActiveTabId();
        if (this.isCopyTab(tabId)) {
            const html = this.getCopyHtml(tabId);
            if (html) return this.clearClassnames(html);
        }

        const defaultCopyHtml = this.parent.text_transcriber?.html().trim();

        return this.clearClassnames(defaultCopyHtml) ?? "";
    }

    clearClassnames(inlineHTML) {
        if (!inlineHTML || typeof inlineHTML !== "string") return inlineHTML;

        return inlineHTML.replace(/class="active"/g, "");
    }

    changeActiveTabById(tabId) {
        if (!tabId) return;
        const desktopTab = this.list?.find?.(`[data-id="${tabId}"]`);
        if (desktopTab) this.changeActiveTab(desktopTab);
    }

    syncMobileDropdown() {
        const activeId = this.getActiveTabId();
        this.mobileSelect?.setActiveById?.(activeId);
        this.mobileSelect?.syncButtonLabelFromDesktop?.(
            this.getActiveTabElement()
        );
    }

    bindAiTemplates() {
        if (!this.modalContent?.categories) return;
        this.modalContent.categories?.forEach((category) => {
            category.items?.forEach((item) => {
                if (item.promtBtn) {
                    item.promtBtn.on("click", (ev) => {
                        ev.preventDefault();
                        this.createAiTab(item);
                    });
                }
            });
        });

        if (this.modalContent.templateViewer?.btnUse) {
            this.modalContent.templateViewer.btnUse.on("click", (ev) => {
                ev.preventDefault();
                const url =
                    this.modalContent.templateViewer.btnUse.attr("href");
                let foundItem = null;

                this.modalContent.categories?.forEach((category) => {
                    category.items?.forEach((item) => {
                        if (item.promtBtn && item.promtBtn.url === url) {
                            foundItem = item;
                        }
                    });
                });

                if (foundItem) {
                    this.createAiTab(foundItem);
                } else {
                    this.createAiTab({
                        promtBtn: { url: url },
                    });
                }
            });
        }

        document.addEventListener("click", (e) => {
            const useBtn = e.target.closest(".use-promt");

            if (useBtn && !useBtn.hasAttribute("data-tbx-processed")) {
                e.preventDefault();
                e.stopPropagation();

                let url =
                    useBtn.getAttribute("data-tbx-var-url") ||
                    useBtn.getAttribute("href") ||
                    useBtn.getAttribute("data-url");

                if (url) {
                    useBtn.setAttribute("data-tbx-processed", "true");
                    this.createAiTab({
                        promtBtn: { url: url },
                    });
                }
            }
        });
    }

    setButtonsLoading(isLoading) {
        const viewerBtn = this.modalContent?.templateViewer?.btnUse;
        if (viewerBtn) {
            isLoading
                ? viewerBtn.removeClass("loading")
                : viewerBtn.addClass("loading");
        }

        const categories = this.modalContent?.categories;
        if (!categories) return;

        for (const category of categories) {
            const items = category.items;
            if (!items) continue;

            for (const item of items) {
                const btn = item.promtBtn;
                if (btn) {
                    isLoading
                        ? btn.removeClass("loading")
                        : btn.addClass("loading");
                }
            }
        }

        const nativeButtons = document.querySelectorAll(
            ".modal-ai-templates__btn-use"
        );

        nativeButtons.forEach((button) => {
            if (isLoading) {
                button.classList.remove("loading");
            } else {
                button.classList.add("loading");
            }
        });

        if (isLoading) this.modalAI?.hide();
    }
}
